"use client";

import { ArrowUpRight, CircuitBoard, Cpu, Layers, Microchip, Package, Wrench } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SixCoreMarkingServices() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: introRef,
    isVisible: introVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: gridRef,
    isVisible: gridVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const headingWords = ["Permanent.", "Compliant.", "Specified."];
  const capabilities = [{
    index: "01",
    title: "Phenolic Nameplates",
    description: "Industrial-grade phenolic legend plates. MIL-SPEC compliant, UV-stable, and resistant to harsh chemical environments. Precision-engraved for long-term legibility.",
    icon: CircuitBoard,
    span: "lg:col-span-5",
    featured: true
  }, {
    index: "02",
    title: "Metal Engraving",
    description: "Stainless steel, anodized aluminum, and brass identification. Deep-engraved for permanent asset tracking and valve identification.",
    icon: Package,
    span: "lg:col-span-3",
    featured: false
  }, {
    index: "03",
    title: "Acrylic signs and applications",
    description: "Custom-fabricated acrylic signage for industrial and commercial use. High-visibility, multi-layered materials for safety instructions and facility branding.",
    icon: Layers,
    span: "lg:col-span-3",
    featured: false
  }, {
    index: "04",
    title: "Direct Part Marking",
    description: "Laser-etched identification directly onto components. Traceability for aerospace, medical, and industrial manufacturing.",
    icon: Microchip,
    span: "lg:col-span-3",
    featured: false
  }, {
    index: "05",
    title: "Electrical Schedules",
    description: "Custom-engraved panel schedules and directory plates. Engineered for clarity and durability in complex electrical distribution systems.",
    icon: Cpu,
    span: "lg:col-span-4",
    featured: true
  }, {
    index: "06",
    title: "Asset & Valve Tags",
    description: "Lockout/tagout and valve identification tags. Built to withstand extreme temperatures and industrial wear.",
    icon: Wrench,
    span: "lg:col-span-4",
    featured: false
  }];
  return <section data-section-id="5197" id="six-core-marking-services" className="bg-background text-foreground py-24 md:py-32 lg:py-44 px-6 md:px-10 lg:px-16 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 mb-16 md:mb-24 lg:mb-32">
          <div ref={headerRef} className={`lg:col-span-7 lg:col-start-1 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase mb-8 md:mb-10" style={{
            color: "var(--chart-1)"
          }}>
              SERVICES · 06
            </p>
            <h2 className="font-default font-bold text-4xl md:text-5xl lg:text-6xl xl:text-[64px] leading-[1.05] tracking-tight text-foreground">
              {headingWords.map((word, i) => <span key={i} data-index={i} className="inline-block overflow-hidden mr-3 md:mr-4">
                  <span className={`inline-block transition-all ease-out ${headerVisible ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"}`} style={{
                transitionDuration: "900ms",
                transitionDelay: `${200 + i * 150}ms`
              }}>
                    {word}
                  </span>
                </span>)}
            </h2>
          </div>

          <div ref={introRef} className={`lg:col-span-3 lg:col-start-9 lg:pt-20 transition-all duration-1000 delay-300 ease-out ${introVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-base md:text-lg leading-relaxed text-foreground/70 font-default font-normal">
              There are six core services we provide to ensure your industrial identification meets every regulatory and operational standard.
            </p>
          </div>
        </div>

        {}
        <div ref={gridRef} className="grid grid-cols-1 lg:grid-cols-12 gap-5 md:gap-6">
          {capabilities.map((cap, i) => {
          const Icon = cap.icon;
          return <Card data-index={i} key={cap.index} className={`group relative ${cap.span} p-8 md:p-10 border border-border bg-card text-card-foreground rounded-none transition-all duration-500 ease-out hover:-translate-y-1 cursor-pointer overflow-hidden ${gridVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"} ${cap.featured ? "lg:min-h-[440px]" : "lg:min-h-[320px]"}`} style={{
            transitionDelay: gridVisible ? `${i * 100}ms` : "0ms",
            borderColor: "var(--border)"
          }} onMouseEnter={e => {
            e.currentTarget.style.borderColor = "var(--chart-1)";
          }} onMouseLeave={e => {
            e.currentTarget.style.borderColor = "var(--border)";
          }}>
                {}
                <span className="absolute top-6 right-7 font-mono text-xs tracking-widest" style={{
              color: "var(--chart-1)"
            }}>
                  {cap.index}
                </span>

                <div className="flex flex-col h-full">
                  {}
                  <div className={`flex items-center justify-center rounded-none mb-8 transition-colors duration-500 ${cap.featured ? "h-32 md:h-40" : "h-20 md:h-24"}`} style={{
                backgroundColor: "var(--muted)"
              }}>
                    <Icon className={`${cap.featured ? "size-16 md:size-20" : "size-10 md:size-12"} transition-transform duration-500 group-hover:scale-110`} style={{
                  color: "var(--chart-1)"
                }} strokeWidth={1.25} />
                  </div>

                  {}
                  <h3 className={`font-default font-bold leading-tight mb-4 transition-colors duration-300 text-foreground group-hover:text-[var(--chart-1)] ${cap.featured ? "text-2xl md:text-3xl" : "text-xl md:text-2xl"}`}>
                    {cap.title}
                  </h3>

                  {}
                  <p className="text-sm leading-relaxed text-foreground/65 font-default mb-6 flex-grow">
                    {cap.description}
                  </p>

                  {}
                  <Link to="/CapabilitiesPage" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.15em] uppercase text-foreground transition-colors duration-300 group-hover:text-[var(--chart-1)]">
                    <span>View Specs</span>
                    <ArrowUpRight className="size-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </Link>
                </div>
              </Card>;
        })}
        </div>

        {}
        <div className="mt-20 md:mt-24 flex flex-col md:flex-row items-start md:items-center justify-between gap-6 pt-12 border-t border-border">
          <p className="font-default font-normal text-xl md:text-2xl text-foreground/80 max-w-xl leading-snug">
            Submit your specifications for a direct quote.
          </p>
          <Button asChild size="lg" className="px-8 h-12 font-mono text-xs tracking-[0.15em] uppercase bg-accent text-accent-foreground hover:bg-accent/90">
            <Link to="/ContactPage">
              Request Quote
              <ArrowUpRight className="size-4 ml-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}